/**
 * Add or edit a finance rule.
 */

import { createPortal } from 'react-dom';
import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { CONTRACT_TYPES, ContractTypeId, FinanceRule, FinanceRuleInput } from '../../../services/api/ruleService';
import { FinanceRuleSeverity } from '../../../types/contract.types';
import { SEVERITY_OPTIONS, contractTypeLabel } from './ruleBookConstants';

interface RuleFormModalProps {
  /** The rule being edited, or null to add a new one. */
  rule: FinanceRule | null;
  /** Prefill for a new rule, e.g. from an AI suggestion. */
  initial?: FinanceRuleInput;
  heading?: string;
  submitLabel?: string;
  categories: string[];
  saving: boolean;
  error?: string | null;
  onSave: (input: FinanceRuleInput) => void;
  onClose: () => void;
}

const EMPTY: FinanceRuleInput = {
  title: '',
  ruleText: '',
  severity: 'HIGH_RISK',
  category: '',
  appliesTo: [],
  suggestedPosition: ''
};

const inputClass =
  'w-full rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400';

const FieldLabel: React.FC<{ htmlFor?: string; children: React.ReactNode; hint?: string }> = ({ htmlFor, children, hint }) => (
  <label htmlFor={htmlFor} className="block mb-1.5">
    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{children}</span>
    {hint && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{hint}</span>}
  </label>
);

export const RuleFormModal: React.FC<RuleFormModalProps> = ({
  rule, initial, heading, submitLabel, categories, saving, error, onSave, onClose
}) => {
  const [form, setForm] = useState<FinanceRuleInput>(EMPTY);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setForm(rule ? {
      title: rule.title,
      ruleText: rule.ruleText,
      severity: rule.severity,
      category: rule.category,
      appliesTo: rule.appliesTo || [],
      suggestedPosition: rule.suggestedPosition || '',
      version: rule.version
    } : { ...EMPTY, ...initial });
    setTouched(false);
  }, [rule, initial]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const set = <K extends keyof FinanceRuleInput>(key: K, value: FinanceRuleInput[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const toggleType = (type: ContractTypeId) =>
    set('appliesTo', form.appliesTo.includes(type)
      ? form.appliesTo.filter(t => t !== type)
      : [...form.appliesTo, type]);

  const missing = {
    title: !form.title.trim(),
    ruleText: !form.ruleText.trim(),
    category: !form.category.trim()
  };
  const valid = !missing.title && !missing.ruleText && !missing.category;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    onSave({
      ...form,
      title: form.title.trim(),
      ruleText: form.ruleText.trim(),
      category: form.category.trim(),
      suggestedPosition: form.suggestedPosition?.trim() || ''
    });
  };

  // Portalled to <body>: rendered in place, the overlay picks up layout from its
  // parent (e.g. a space-y margin) and stops covering the whole window.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !saving && onClose()}>
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="rule-form-title"
        onClick={e => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl bg-white dark:bg-dark-surface shadow-xl"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-dark-border">
          <h2 id="rule-form-title" className="text-lg font-semibold text-gray-900 dark:text-white">
            {heading ?? (rule ? 'Edit rule' : 'Add rule')}
          </h2>
          <button type="button" onClick={onClose} disabled={saving} aria-label="Close" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div>
            <FieldLabel htmlFor="rule-title">Title</FieldLabel>
            <input
              id="rule-title"
              className={inputClass}
              value={form.title}
              maxLength={150}
              placeholder="Payment terms no longer than Net 60"
              onChange={e => set('title', e.target.value)}
              autoFocus
            />
            {touched && missing.title && <p className="mt-1 text-xs text-red-600">Give the rule a title.</p>}
          </div>

          <div>
            <FieldLabel
              htmlFor="rule-text"
              hint="Write it the way you'd explain it to a colleague. The AI checks every contract against this wording."
            >
              Rule
            </FieldLabel>
            <textarea
              id="rule-text"
              className={`${inputClass} min-h-[96px]`}
              value={form.ruleText}
              maxLength={2000}
              placeholder="Payment terms longer than 60 days from invoice are not acceptable."
              onChange={e => set('ruleText', e.target.value)}
            />
            {touched && missing.ruleText && <p className="mt-1 text-xs text-red-600">Write the rule.</p>}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <FieldLabel htmlFor="rule-severity" hint="How serious a breach is. The AI never changes this.">
                Severity if breached
              </FieldLabel>
              <select
                id="rule-severity"
                className={`${inputClass} pr-9`}
                value={form.severity}
                onChange={e => set('severity', e.target.value as FinanceRuleSeverity)}
              >
                {SEVERITY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label} - {o.description}</option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel htmlFor="rule-category" hint="Groups rules in the rule book, e.g. Payment, Liability.">
                Category
              </FieldLabel>
              <input
                id="rule-category"
                className={inputClass}
                list="rule-categories"
                value={form.category}
                maxLength={60}
                placeholder="Payment"
                onChange={e => set('category', e.target.value)}
              />
              <datalist id="rule-categories">
                {categories.map(c => <option key={c} value={c} />)}
              </datalist>
              {touched && missing.category && <p className="mt-1 text-xs text-red-600">Pick or type a category.</p>}
            </div>
          </div>

          <fieldset>
            <legend className="mb-1.5">
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Applies to</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {form.appliesTo.length === 0
                  ? 'All contract types. Select types to narrow it.'
                  : `Only ${form.appliesTo.length} selected type${form.appliesTo.length === 1 ? '' : 's'}.`}
              </span>
            </legend>
            <div className="flex flex-wrap gap-2">
              {CONTRACT_TYPES.map(type => {
                const on = form.appliesTo.includes(type);
                return (
                  <button
                    type="button"
                    key={type}
                    aria-pressed={on}
                    onClick={() => toggleType(type)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      on
                        ? 'bg-primary-600 border-primary-600 text-white dark:bg-accent-500 dark:border-accent-500 dark:text-gray-900'
                        : 'border-gray-300 dark:border-dark-border text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-secondary'
                    }`}
                  >
                    {contractTypeLabel(type)}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div>
            <FieldLabel htmlFor="rule-position" hint="Optional. Shown next to a breach as what to ask for instead.">
              Suggested position
            </FieldLabel>
            <textarea
              id="rule-position"
              className={`${inputClass} min-h-[72px]`}
              value={form.suggestedPosition}
              maxLength={1000}
              placeholder="Push back to Net 45. Net 60 is the maximum we accept."
              onChange={e => set('suggestedPosition', e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 dark:border-dark-border">
          {/* Errors go in the footer, beside the button that caused them: at the end of the
              scrolling body they'd be out of sight and Save would look like it did nothing */}
          {error ? (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">Changes apply to the next analysis that runs.</p>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white dark:bg-accent-500 dark:hover:bg-accent-400 dark:text-gray-900 disabled:opacity-60">
              {saving ? 'Saving...' : submitLabel ?? (rule ? 'Save changes' : 'Add rule')}
            </button>
          </div>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default RuleFormModal;
